import type { ElementNode, LexicalNode, NodeKey } from 'lexical'
import type { AdamLexicalNode } from '../../../../nodes'
import type { MoreOptionsOption } from '../../../../types'
import type { TableRowNode } from '../LexicalTableRowNode/LexicalTableRowNode'
import type { TableCellNode } from '../TableCellNode/TableCellNode'
import { TableCellHeaderStates } from '../TableCellNode/TableCellNodeHeadless'
import type {
    SerializedTableContainerNode,
    TablePayload,
} from './TableContainerNodeHeadless'
import { TableContainerNodeHeadless } from './TableContainerNodeHeadless'

class TableContainerNode extends TableContainerNodeHeadless {
    allowContentPickerInside(): boolean {
        return false
    }

    isDraggable(): boolean {
        return true
    }

    canDropInside(): boolean {
        return false
    }

    isDropTarget(): boolean {
        return true
    }

    canBeLastItem() {
        return false
    }

    canIndent(): boolean {
        return false
    }

    getDefaultDetailsTab() {
        return 'Related'
    }

    getEnabledOptions(): Record<MoreOptionsOption, boolean> {
        return {
            download: false,
            attachment: true,
            comment: true,
            tag: true,
            reaction: true,
            delete: true,
            duplicate: true,
            viewDetails: true,
            integration: false,
            aiPrompts: false,
            rowHeader: true,
            columnHeader: true,
            annotate: false,
            startPresentation: false,
        }
    }

    static getType(): string {
        return 'table-container'
    }

    static clone(node: TableContainerNode): TableContainerNode {
        return $createTableContainerNode(node.getPayload(), node.getKey())
    }

    static importJSON(
        serializedNode: SerializedTableContainerNode,
    ): TableContainerNode {
        return $createTableContainerNode(serializedNode.payload)
    }

    createDOM(): HTMLElement {
        const dom = document.createElement('div')
        dom.classList.add(
            'EditorTheme__contentItemContainer',
            'EditorTheme__tableContainer',
        )
        return dom
    }

    updateDOM(): boolean {
        return false
    }

    exportJSON(): SerializedTableContainerNode {
        return {
            ...super.exportJSON(),
            type: 'table-container',
            payload: this.__payload,
        }
    }

    select(_anchorOffset?: number, _focusOffset?: number) {
        return this.getTextEditableNode().select(_anchorOffset, _focusOffset)
    }

    selectEnd() {
        const table = this.getTableNode()
        return table
            .getLastChildOrThrow<TableRowNode>()
            .getLastChildOrThrow<TableCellNode>()
            .selectEnd()
    }

    toggleHeaderColumn() {
        const tableNode = this.getTableNode()
        const tableRows = tableNode.getChildren()
        for (let i = 0; i < tableRows.length; ++i) {
            const row = tableRows[i] as TableRowNode
            const cell = row.getFirstChild() as TableCellNode
            const headerStyles = cell.getHeaderStyles()
            if (this.__payload.isColumnHeaderOn) {
                if (headerStyles === TableCellHeaderStates.BOTH) {
                    cell.setHeaderStyles(TableCellHeaderStates.ROW)
                } else {
                    cell.setHeaderStyles(TableCellHeaderStates.NO_STATUS)
                }
                this.updatePayload({ isColumnHeaderOn: false })
            } else {
                if (headerStyles === TableCellHeaderStates.ROW) {
                    cell.setHeaderStyles(TableCellHeaderStates.BOTH)
                } else {
                    cell.setHeaderStyles(TableCellHeaderStates.COLUMN)
                }
                this.updatePayload({ isColumnHeaderOn: true })
            }
        }
    }

    toggleHeaderRow() {
        const tableNode = this.getTableNode()
        const firstTableRow = tableNode.getFirstChild() as ElementNode
        const cells = firstTableRow.getChildren()
        for (let i = 0; i < cells.length; ++i) {
            const cell = cells[i] as TableCellNode
            const headerStyles = cell.getHeaderStyles()
            if (this.__payload.isRowHeaderOn) {
                if (headerStyles === TableCellHeaderStates.BOTH) {
                    cell.setHeaderStyles(TableCellHeaderStates.COLUMN)
                } else {
                    cell.setHeaderStyles(TableCellHeaderStates.NO_STATUS)
                }
                this.updatePayload({ isRowHeaderOn: false })
            } else {
                if (headerStyles === TableCellHeaderStates.COLUMN) {
                    cell.setHeaderStyles(TableCellHeaderStates.BOTH)
                } else {
                    cell.setHeaderStyles(TableCellHeaderStates.ROW)
                }
                this.updatePayload({ isRowHeaderOn: true })
            }
        }
    }

    cleanForPaste() {
        this.__payload = {
            ...this.__payload,
            id: '',
            reactions: [],
            commentsCount: 0,
            linksCount: 0,
            tagsCount: 0,
        }
        this.getChildren().forEach((child: AdamLexicalNode) => {
            child.cleanForPaste?.()
        })
    }
}

function $createTableContainerNode(
    payload?: Partial<TablePayload>,
    nodeKey?: NodeKey,
) {
    return new TableContainerNode(payload, nodeKey)
}

const $isTableContainerNode = (
    node: LexicalNode | null | undefined,
): node is TableContainerNode => node instanceof TableContainerNode

export { $createTableContainerNode, $isTableContainerNode, TableContainerNode }
